/* ==========================================================================
   Mawj Liquid Glass — engine (homepage)
   - Tags every section surface (header -> footer) as glass.
   - Frosted baseline on all browsers; SVG-displacement refraction on Chromium.
   - Hover reaction: pointer-following specular sheen (all browsers) +
     "liquid swell" of the refraction on Chromium.
   Technique: feImage (rounded-rect displacement map) -> feDisplacementMap.
   ========================================================================== */
(function () {
  "use strict";

  var root = document.documentElement;
  root.classList.add("mawj-home-glass"); // enables the glass CSS for this page

  var reduceMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
  var reduceTrans = matchMedia("(prefers-reduced-transparency: reduce)").matches;

  // Which surfaces become glass. refract:true => add SVG lensing (Chromium).
  var MAP = [
    { sel: ".mawj-hp__glass", cls: "", refract: true },          // hero (already in markup)
    { sel: ".m-header__desktop", cls: "mawj-glass mawj-glass--soft mawj-glass--flat", refract: false },
    { sel: ".m-header__mobile", cls: "mawj-glass mawj-glass--soft mawj-glass--flat", refract: false },
    { sel: ".mpt__card", cls: "mawj-glass mawj-glass--card", refract: true },
    { sel: ".mawj-pp__inner", cls: "mawj-glass mawj-glass--card", refract: true },
    { sel: ".mawj-ba__inner", cls: "mawj-glass mawj-glass--card", refract: true },
    { sel: ".mawj-st__inner", cls: "mawj-glass mawj-glass--card", refract: true },
    { sel: ".mawj-tt__card", cls: "mawj-glass mawj-glass--card", refract: true },
    { sel: ".mawj-fq__item", cls: "mawj-glass mawj-glass--card mawj-glass--soft", refract: true }
  ];

  // ---- Refraction support (Chromium only) -------------------------------
  var ua = navigator.userAgent;
  var isChromium =
    /Chrome|Chromium|Edg|OPR/.test(ua) &&
    /Google Inc\.|Microsoft/.test(navigator.vendor || "") &&
    !/Apple/.test(navigator.vendor || "");
  var canRefract =
    isChromium &&
    !reduceMotion &&
    !reduceTrans &&
    window.CSS && CSS.supports && CSS.supports("backdrop-filter", "url(#m)");

  var SVGNS = "http://www.w3.org/2000/svg";
  var XLINK = "http://www.w3.org/1999/xlink";
  var BEZEL = 24;
  var SHIFT = 16;     // base displacement (refined, subtle lensing)
  var SWELL = 1.3;    // gentle hover settle
  var MAXRES = 400;

  var host, defs, uid = 0;

  function ensureHost() {
    if (host) return;
    host = document.createElementNS(SVGNS, "svg");
    host.setAttribute("aria-hidden", "true");
    host.setAttribute("width", "0");
    host.setAttribute("height", "0");
    host.setAttribute("color-interpolation-filters", "sRGB");
    host.style.cssText = "position:absolute;width:0;height:0;overflow:hidden;pointer-events:none";
    defs = document.createElementNS(SVGNS, "defs");
    host.appendChild(defs);
    document.body.appendChild(host);
  }

  function sdRoundRect(px, py, hw, hh, r) {
    var qx = Math.abs(px) - hw + r;
    var qy = Math.abs(py) - hh + r;
    var ox = Math.max(qx, 0), oy = Math.max(qy, 0);
    return Math.hypot(ox, oy) + Math.min(Math.max(qx, qy), 0) - r;
  }
  function smoothstep(e0, e1, x) {
    var t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
    return t * t * (3 - 2 * t);
  }

  function buildMap(w, h, radius) {
    var scale = Math.min(1, MAXRES / Math.max(w, h));
    var cw = Math.max(2, Math.round(w * scale));
    var ch = Math.max(2, Math.round(h * scale));
    var bezel = BEZEL * scale;
    var cv = document.createElement("canvas");
    cv.width = cw; cv.height = ch;
    var ctx = cv.getContext("2d");
    var img = ctx.createImageData(cw, ch);
    var data = img.data;
    var hw = cw / 2, hh = ch / 2;
    var r = Math.max(1, radius * scale), d = 1;
    for (var y = 0; y < ch; y++) {
      for (var x = 0; x < cw; x++) {
        var px = x - hw + 0.5, py = y - hh + 0.5;
        var sd = sdRoundRect(px, py, hw, hh, r);
        var mag = sd < 0 ? smoothstep(-bezel, 0, sd) : 0;
        var dx = 0, dy = 0;
        if (mag > 0) {
          var gx = sdRoundRect(px + d, py, hw, hh, r) - sdRoundRect(px - d, py, hw, hh, r);
          var gy = sdRoundRect(px, py + d, hw, hh, r) - sdRoundRect(px, py - d, hw, hh, r);
          var gl = Math.hypot(gx, gy) || 1;
          dx = (-gx / gl) * mag; dy = (-gy / gl) * mag;
        }
        var i = (y * cw + x) * 4;
        data[i] = 128 + dx * 127;
        data[i + 1] = 128 + dy * 127;
        data[i + 2] = 128;
        data[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
    return cv.toDataURL();
  }

  function tween(node, attr, from, to, dur) {
    var startTs = null;
    function step(ts) {
      if (startTs === null) startTs = ts;
      var t = Math.min(1, (ts - startTs) / dur);
      var e = 1 - Math.pow(1 - t, 3);
      node.setAttribute(attr, (from + (to - from) * e).toFixed(2));
      if (t < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function refract(el) {
    var rect = el.getBoundingClientRect();
    if (rect.width < 8 || rect.height < 8) return null;
    ensureHost();
    var id = "mawjGlass" + ++uid;
    var filter = document.createElementNS(SVGNS, "filter");
    filter.setAttribute("id", id);
    filter.setAttribute("color-interpolation-filters", "sRGB");
    var feImg = document.createElementNS(SVGNS, "feImage");
    feImg.setAttribute("preserveAspectRatio", "none");
    feImg.setAttribute("result", "map");
    var feDisp = document.createElementNS(SVGNS, "feDisplacementMap");
    feDisp.setAttribute("in", "SourceGraphic");
    feDisp.setAttribute("in2", "map");
    feDisp.setAttribute("scale", String(SHIFT));
    feDisp.setAttribute("xChannelSelector", "R");
    feDisp.setAttribute("yChannelSelector", "G");
    filter.appendChild(feImg);
    filter.appendChild(feDisp);
    defs.appendChild(filter);

    function render() {
      var r = el.getBoundingClientRect();
      if (r.width < 8 || r.height < 8) return;
      var rad = parseFloat(getComputedStyle(el).borderTopLeftRadius) || 18;
      var url = buildMap(r.width, r.height, rad);
      feImg.setAttribute("width", String(r.width));
      feImg.setAttribute("height", String(r.height));
      feImg.setAttributeNS(XLINK, "href", url);
      feImg.setAttribute("href", url);
      el.style.backdropFilter = "blur(2px) saturate(1.4) url(#" + id + ")";
      el.style.webkitBackdropFilter = el.style.backdropFilter;
      el.classList.add("is-refracting");
    }
    render();

    var t;
    new ResizeObserver(function () {
      clearTimeout(t);
      t = setTimeout(render, 140);
    }).observe(el);

    return feDisp;
  }

  function attachHover(el, feDisp) {
    if (!feDisp) return;
    el.addEventListener("pointerenter", function () {
      tween(feDisp, "scale", parseFloat(feDisp.getAttribute("scale")), SHIFT * SWELL, 650);
    });
    el.addEventListener("pointerleave", function () {
      tween(feDisp, "scale", parseFloat(feDisp.getAttribute("scale")), SHIFT, 800);
    });
  }

  // Tactile crystal tilt toward the cursor (hover devices, motion-OK only)
  function tiltCards() {
    if (reduceMotion || !matchMedia("(hover: hover)").matches) return;
    document.querySelectorAll(".mpt__card, .mawj-tt__card").forEach(function (card) {
      card.classList.add("mawj-tilt");
      var raf;
      card.addEventListener("pointermove", function (e) {
        var r = card.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          card.style.transition = "transform 0s";
          card.style.transform =
            "perspective(900px) rotateX(" + (-py * 5).toFixed(2) +
            "deg) rotateY(" + (px * 5).toFixed(2) + "deg) translateY(-3px)";
        });
      });
      card.addEventListener("pointerleave", function () {
        cancelAnimationFrame(raf);
        card.style.transition = "";
        card.style.transform = "";
      });
    });
  }

  function init() {
    MAP.forEach(function (entry) {
      document.querySelectorAll(entry.sel).forEach(function (el) {
        if (entry.cls) el.className += " " + entry.cls;
        var feDisp = null;
        if (entry.refract && canRefract) feDisp = refract(el);
        attachHover(el, feDisp);
      });
    });
    tiltCards();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  window.MawjGlass = { refresh: init };
})();
